/**
 * Hunger and rest (M3-06) — the meters, and what each band of them is worth.
 *
 * The scoping decision is the most important thing in this file, and it is the
 * ticket's own: **hunger in the MVP is pressure, not a death spiral.** A tester
 * who logs in twice a week must not find a corpse. So the meter drains, the
 * bands bite, and then it *stops* — `starving` is a floor, not a slope toward
 * zero, and there is no code path anywhere that turns an empty belly into a
 * death. What hunger is really for is feeding the medicine system: blood
 * regeneration (M1-04) and recovery (M4-04) run off fed-and-rested state, which
 * is what makes a camp the place the wounded get whole.
 *
 * Two meters, deliberately different shapes:
 *
 * - **Food is a reservoir.** It fills when you eat and drains with the clock.
 *   Named bands, never a percentage shown to a player — the same discipline
 *   blood, stamina, freshness and carcasses already follow.
 * - **Rest is a property of a place, not a reservoir.** There is no "rested
 *   meter" to grind back up; there is the ground you are lying on and whether
 *   there is a fire on it. That is what makes a camp a *location decision*
 *   rather than a resource to manage, and it is why the four tiers are named
 *   after places.
 *
 * Depends on nothing but the clock, because `world.ts` has to name `Needs` in
 * order to store it and `vitals.ts` has to read the bands in order to apply
 * them. The rules that read world state live in `camp.ts`.
 */
import { TICKS_PER_GAME_DAY } from './time.js';
/* -------------------------------------------------------------------------- *
 * Food
 * -------------------------------------------------------------------------- */
/**
 * A full belly, in thousandths.
 *
 * Integers for the reason blood and wetness are integers: world state is hashed
 * for determinism, and a float accumulated tick by tick over a fortnight is a
 * fingerprint that drifts.
 */
export const MAX_FOOD = 1000;
/**
 * How much of a full belly a game day costs.
 *
 * A bit over two days from full to empty. Long enough that eating is not a
 * chore at ten ticks a second — a game day is one real hour, so a well-fed
 * player is good for two hours of play — and short enough that a session which
 * never stops to eat notices.
 *
 * 480 rather than a round 500 so that it divides exactly by the 24 game hours
 * in a day: the meter is drained on the hour and stored as an integer, and a
 * rate that did not divide would round up 24 times a day and make hourly
 * draining measurably faster than the daily figure it is supposed to be. The
 * same class of bug `bleedCarry` exists to prevent, avoided here by choosing a
 * number instead of carrying a remainder.
 */
export const FOOD_PER_DAY = 480;
/**
 * The hunger ladder. Named stages, in order, worst last.
 *
 * A player reads one of three words and never a number, which is the same call
 * `BLOOD_STAGES`, `STAMINA_BANDS`, `FRESHNESS_STAGES` and `CARCASS_STAGES` all
 * make. There is no fourth band below `starving` on purpose: see the header.
 */
export const HUNGER_BANDS = ['fed', 'hungry', 'starving'];
/** Food at or below which each band begins, as a fraction of a full belly. */
export const HUNGER_THRESHOLDS = {
    hungry: 0.5,
    starving: 0.15,
};
export function hungerBand(food) {
    const fraction = food / MAX_FOOD;
    if (fraction <= HUNGER_THRESHOLDS.starving)
        return 'starving';
    if (fraction <= HUNGER_THRESHOLDS.hungry)
        return 'hungry';
    return 'fed';
}
/**
 * What each band multiplies the stamina cap by (the ticket's −25% / −50%).
 *
 * The cap rather than the regeneration rate, and the difference matters in a
 * fight: a hungry man does not run out of wind faster, he has less of it to
 * begin with, and he can feel that in the first exchange rather than the sixth.
 */
export const HUNGER_STAMINA = {
    fed: 1,
    hungry: 0.75,
    starving: 0.5,
};
/**
 * What each band multiplies blood regeneration by — `Vitals.nourishment`, which
 * has been sitting in `vitals.ts` since M1-04 with a note saying M3-06 would
 * one day have something real to say. This is that.
 */
export const HUNGER_NOURISHMENT = {
    fed: 1,
    hungry: 0.5,
    starving: 0,
};
/**
 * Dice a starving character loses to the ache of it.
 *
 * Charged as *pain* rather than as a further cut to the pool, because that is
 * what it is: hunger past a certain point hurts, and the game already has a
 * clock for things that hurt. It also means grit reduces it, which is right —
 * a tough man carries an empty stomach better.
 */
export const HUNGER_PAIN = {
    fed: 0,
    hungry: 0,
    starving: 1,
};
/* -------------------------------------------------------------------------- *
 * Rest
 * -------------------------------------------------------------------------- */
/**
 * Where you slept, worst first. A property of the *place*, not a meter.
 *
 * The ordering is the ticket's, and each tier is a thing a player can see
 * without being told a number: `exposed` is wet ground or open moor in the
 * rain; `dry` is anywhere with the weather off it; `fire` is inside the ring of
 * one; `bed` is the village, under a roof, beside the hearth.
 */
export const REST_QUALITIES = ['exposed', 'dry', 'fire', 'bed', 'cot'];
/**
 * What each tier multiplies recovery by.
 *
 * `exposed` is **zero**, and that is the ticket's fourth acceptance criterion
 * stated as a table: a hungry character on wet ground regenerates nothing at
 * all (0.5 × 0), while a fed one at a fire regenerates at the full rate
 * (1 × 1). Sleeping rough in the rain is not slow healing, it is no healing —
 * which is the whole argument for building a camp.
 */
export const REST_RECOVERY = {
    exposed: 0,
    dry: 0.5,
    fire: 1,
    bed: 1.25,
    // M4-08's cot: twice a fire, and the best thing in the valley by some way.
    // The ticket asks for ×2 and one occupant, and both halves matter — a
    // healer's bed anybody could share would be a healer's bed nobody argues
    // over, and the argument is what makes her house a place.
    cot: 2,
};
/**
 * How much faster recovery accrues while actually lying down asleep.
 *
 * Resting does not unlock recovery — where you are does that — it *concentrates*
 * it: eight game hours asleep is worth about a day of walking around in the
 * same place. What it costs is that you are on the ground and cannot see
 * anybody coming, which is the trade the ticket wants guard rotation to emerge
 * from.
 */
export const REST_SPEEDUP = 3;
/** How long `well-rested` lasts after a proper sleep. Half a game day. */
export const WELL_RESTED_TICKS = TICKS_PER_GAME_DAY / 2;
/**
 * What `well-rested` is worth: you get your wind back half again as fast.
 *
 * Deliberately a *regeneration* bonus rather than a bigger stamina cap. A cap
 * bonus would have to be read by `maxStamina`, which knows about a body and not
 * about a bed, and would mean a second derived number living on `Vitals` for
 * one gentle nudge. This lives entirely in `camp.ts` and is felt exactly where
 * it should be — in the second minute of a fight, not the first.
 */
export const WELL_RESTED_REGEN = 0.5;
export function createNeeds(food = MAX_FOOD) {
    return {
        food: clampFood(food),
        restingSince: 0,
        restX: 0,
        restY: 0,
        restedUntil: 0,
        illUntil: 0,
        reliefUntil: 0,
        reliefPain: 0,
        reliefFever: 0,
    };
}
export function clampFood(value) {
    if (!Number.isFinite(value))
        return 0;
    return Math.max(0, Math.min(MAX_FOOD, Math.round(value)));
}
/** Eat. Returns how much of the meal was actually room for. */
export function nourish(needs, amount) {
    if (amount <= 0)
        return 0;
    const before = needs.food;
    needs.food = clampFood(before + amount);
    return needs.food - before;
}
/**
 * Burn through some food, over a span of ticks.
 *
 * Floors at zero rather than going negative, which is the death spiral this
 * ticket exists to refuse: there is nothing below `starving` for the meter to
 * reach and nothing that reads a number below zero.
 */
export function starve(needs, ticks, rate = 1) {
    // `rate` is M4-07's Cold-Hungered: a multiplier on how fast the meter runs,
    // passed in by the caller that knows what marks the body bears. Defaulted, so
    // every caller that has never heard of the cauldron reads exactly as before.
    needs.food = clampFood(needs.food - (FOOD_PER_DAY * ticks * rate) / TICKS_PER_GAME_DAY);
}
export function isResting(needs) {
    return needs.restingSince > 0;
}
export function isWellRested(needs, tick) {
    return tick < needs.restedUntil;
}
export function isIll(needs, tick) {
    return tick < needs.illUntil;
}
/* -------------------------------------------------------------------------- *
 * What a remedy is worth (M4-03)
 * -------------------------------------------------------------------------- */
export function isRelieved(needs, tick) {
    return tick < needs.reliefUntil && (needs.reliefPain > 0 || needs.reliefFever > 0);
}
/**
 * Take something for the pain. Returns whether it changed anything.
 *
 * Relief does not *stack* — a second draught while the first is working takes
 * the better of the two and the later deadline, rather than adding. Two mouthfuls
 * of willow are not twice the willow, and a rule that said they were would make
 * the correct play "drink the whole satchel", which is the shape of exploit a
 * consumable buff invites and this game has no use for.
 */
export function takeRelief(needs, tick, pain, fever, ticks) {
    if (ticks <= 0 || (pain <= 0 && fever <= 0))
        return false;
    const active = isRelieved(needs, tick);
    needs.reliefPain = active ? Math.max(needs.reliefPain, pain) : pain;
    needs.reliefFever = active ? Math.max(needs.reliefFever, fever) : fever;
    needs.reliefUntil = Math.max(active ? needs.reliefUntil : 0, tick + Math.round(ticks));
    return true;
}
/** Clear a remedy that has worn off. Returns whether anything was cleared. */
export function expireRelief(needs, tick) {
    if (tick < needs.reliefUntil)
        return false;
    if (needs.reliefUntil === 0 && needs.reliefPain === 0 && needs.reliefFever === 0)
        return false;
    needs.reliefUntil = 0;
    needs.reliefPain = 0;
    needs.reliefFever = 0;
    return true;
}
/**
 * How ill a bad meal makes you, as `Vitals.fever`.
 *
 * A third of what an infection's `fevered` stage is worth, and time-limited.
 * Enough to notice in a fight and nowhere near enough to be dangerous — the
 * ticket's "never lethal alone", stated as a number.
 */
export const SICKNESS_FEVER = 0.2;
/** How long a bad meal lasts: a few game hours, not a few days. */
export const SICKNESS_TICKS = TICKS_PER_GAME_DAY / 6;
/** What hunger reads as, for the examine line and the status strip. */
export function hungerProse(band) {
    switch (band) {
        case 'hungry':
            return 'hungry';
        case 'starving':
            return 'starving';
        default:
            return 'fed';
    }
}
//# sourceMappingURL=needs.js.map